import { supabase } from '../lib/supabaseClient'
import type { PlanType } from '../types/payment'

/**
 * Server-driven checkout. The client only says WHAT the user wants (plan,
 * scan, where they tapped). The `start-checkout` Edge Function decides the
 * price, currency, customer email, metadata and callback URL.
 */

export const CHECKOUT_PLANS: readonly PlanType[] = [
  'single_scan',
  'weekly',
  'monthly',
  'yearly',
]

export function isCheckoutPlan(value: unknown): value is PlanType {
  return typeof value === 'string' && (CHECKOUT_PLANS as readonly string[]).includes(value)
}

export type CheckoutErrorCode =
  | 'already_unlocked'
  | 'already_pro'
  | 'scan_not_ready'
  | 'rate_limited'
  | 'missing_scan'
  | 'session'
  | 'unknown'

export type StartCheckoutResult =
  | { ok: true; authorizationUrl: string }
  | { ok: false; code: CheckoutErrorCode; message: string }

export const CHECKOUT_ERROR_COPY: Record<CheckoutErrorCode, string> = {
  already_unlocked: 'This report is already unlocked',
  already_pro: 'You already have Pro — no payment needed.',
  scan_not_ready: 'This report is still being prepared. Try again once the analysis finishes.',
  rate_limited: 'Please wait a few minutes before trying again.',
  missing_scan:
    'Single-report unlocks apply to a finished scan. Run your scan, then unlock it from the report.',
  session: 'Your session expired. Sign in again to continue to checkout.',
  unknown: 'Checkout couldn’t be started. No payment was taken — please try again.',
}

interface StartCheckoutInput {
  plan: PlanType
  /** Required for single_scan; ignored for subscriptions. */
  scanId?: string | null
  /** Where the button lives, e.g. "upload_paywall", "pricing_section". */
  location: string
}

const KNOWN_CODES: CheckoutErrorCode[] = [
  'already_unlocked',
  'already_pro',
  'scan_not_ready',
  'rate_limited',
]

function codeFromText(value: unknown): CheckoutErrorCode | null {
  if (typeof value !== 'string') return null
  const normalized = value.toLowerCase()
  const exact = KNOWN_CODES.find((code) => normalized === code)
  if (exact) return exact
  if (/already[_\s-]?unlocked/.test(normalized)) return 'already_unlocked'
  if (/already[_\s-]?pro|already subscribed/.test(normalized)) return 'already_pro'
  if (/scan[_\s-]?not[_\s-]?ready|not ready|still processing/.test(normalized)) return 'scan_not_ready'
  if (/rate[_\s-]?limit|too many requests/.test(normalized)) return 'rate_limited'
  return null
}

function classifyBody(body: unknown): CheckoutErrorCode | null {
  if (!body || typeof body !== 'object') return codeFromText(body)
  const record = body as Record<string, unknown>
  return (
    codeFromText(record.code) ??
    codeFromText(record.error_code) ??
    codeFromText(record.error) ??
    codeFromText(record.message)
  )
}

function extractUrl(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null
  const record = data as Record<string, unknown>
  if (typeof record.authorization_url === 'string') return record.authorization_url
  const nested = record.data
  if (nested && typeof nested === 'object') {
    const url = (nested as Record<string, unknown>).authorization_url
    if (typeof url === 'string') return url
  }
  return null
}

async function classifyInvokeError(error: unknown): Promise<CheckoutErrorCode> {
  const context = (error as { context?: unknown } | null)?.context
  if (typeof Response !== 'undefined' && context instanceof Response) {
    if (context.status === 429) return 'rate_limited'
    if (context.status === 401) return 'session'
    try {
      const body: unknown = await context.clone().json()
      const code = classifyBody(body)
      if (code) return code
    } catch {
      /* non-JSON body */
    }
  }
  if (error instanceof Error) {
    const code = codeFromText(error.message)
    if (code) return code
  }
  return 'unknown'
}

function fail(code: CheckoutErrorCode): StartCheckoutResult {
  return { ok: false, code, message: CHECKOUT_ERROR_COPY[code] }
}

export async function startCheckout({
  plan,
  scanId,
  location,
}: StartCheckoutInput): Promise<StartCheckoutResult> {
  if (!isCheckoutPlan(plan)) return fail('unknown')
  if (plan === 'single_scan' && !scanId) return fail('missing_scan')

  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session) return fail('session')

  const body: Record<string, string> = {
    plan,
    location,
    page: typeof window !== 'undefined' ? window.location.pathname : '',
  }
  if (plan === 'single_scan' && scanId) body.scan_id = scanId

  try {
    const { data, error } = await supabase.functions.invoke('start-checkout', {
      body,
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (error) return fail(await classifyInvokeError(error))
    const url = extractUrl(data)
    if (url) return { ok: true, authorizationUrl: url }
    return fail(classifyBody(data) ?? 'unknown')
  } catch (err) {
    return fail(await classifyInvokeError(err))
  }
}

/** Full-page redirect — more reliable than a popup, especially on mobile. */
export function redirectToCheckout(authorizationUrl: string): void {
  window.location.assign(authorizationUrl)
}
